import type { CalendarModel } from '@bryntum/schedulerpro';
import { unreachable } from 'utils/unreachable';
import {
  EventModelConfigWithoutMandatoryFields,
  SchedulerEventType,
} from '../../event-types';

export const getSchedulerEventsClass = (
  type: SchedulerEventType,
  classToAppend = '',
): string => {
  switch (type) {
    case SchedulerEventType.Allocation:
      return `allocation ${classToAppend}`;
    case SchedulerEventType.RequestedAllocation:
      return `requested_allocation ${classToAppend}`;
    case SchedulerEventType.UserAllocationSummary:
      return `user_allocation_summary ${classToAppend}`;
    case SchedulerEventType.TeamAllocationSummary:
      return `team_allocation_summary ${classToAppend}`;
    case SchedulerEventType.CompanyAllocationSummary:
      return `company_allocation_summary ${classToAppend}`;
    case SchedulerEventType.ProjectTimeline:
      return `project-timeline ${classToAppend}`;
    case SchedulerEventType.ProjectAsALeafTimeline:
      return `project_as_a_leaf_timeline ${classToAppend}`;
    case SchedulerEventType.BudgetAsALeafTimeline:
      return `budget_as_a_leaf_timeline ${classToAppend}`;
    case SchedulerEventType.ProjectPhase:
      return `project-phase ${classToAppend}`;
    case SchedulerEventType.DemandRole:
      return `demand_role ${classToAppend}`;
    case SchedulerEventType.DemandCapacity:
      return `demand_capacity ${classToAppend}`;
    case SchedulerEventType.TotalAllocation:
      return `total_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalAllocation:
      return `user_total_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalSoftAllocation:
      return `user_total_soft_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalHardAllocation:
      return `user_total_hard_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalAllocation:
      return `team_total_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalSoftAllocation:
      return `team_total_soft_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalHardAllocation:
      return `team_total_hard_allocation ${classToAppend}`;
    case SchedulerEventType.UserTimeOff:
      return `time-off-event ${classToAppend}`;
    case SchedulerEventType.UserFte:
      return `user_fte ${classToAppend}`;
    case SchedulerEventType.PartnerFte:
      return `partner_fte ${classToAppend}`;
    case SchedulerEventType.TimeOffFte:
      return `timeoff_fte ${classToAppend}`;
    case SchedulerEventType.HolidayFte:
      return `holiday_fte ${classToAppend}`;
    case SchedulerEventType.CapacityAllocationDiffFte:
      return `capacity_allocation_diff_fte ${classToAppend}`;
    case SchedulerEventType.ProjectsAllocationInRoleFte:
      return `projects_allocation_fte ${classToAppend}`;
    case SchedulerEventType.Holiday:
      return `holiday-event ${classToAppend}`;
    case SchedulerEventType.ProjectBudgetTimeline:
      return `budget-timeline ${classToAppend}`;
    case SchedulerEventType.ProjectAllocationSummary:
      return `user_project_allocation_summary ${classToAppend}`;
    case SchedulerEventType.BudgetAllocationSummary:
      return `budget_allocation_summary ${classToAppend}`;
    case SchedulerEventType.ResourceAllocationSummary:
      return `resource_allocation_summary ${classToAppend}`;
    default:
      return unreachable(type);
  }
};

const ReadOnlyEventProps = {
  calendar: '24h' as unknown as CalendarModel,
  resizable: false,
  draggable: false,
  manuallyScheduled: true,
};

export const getSchedulerEventProps = (
  type: SchedulerEventType,
  defaultValues: Partial<EventModelConfigWithoutMandatoryFields> = {},
): Partial<EventModelConfigWithoutMandatoryFields> => {
  switch (type) {
    case SchedulerEventType.Allocation:
      return {
        eventColor: 'var(--blue-20)',
        cls: getSchedulerEventsClass(type),
        calendar: '24h' as unknown as CalendarModel,
        stickyContents: false,
        ...defaultValues,
      };
    case SchedulerEventType.RequestedAllocation:
      return {
        eventColor: 'var(--cyan-10)',
        cls: getSchedulerEventsClass(type),
        calendar: '24h' as unknown as CalendarModel,
        eventStyle: 'border: 1px dashed var(--cyan-40);',
        stickyContents: false,
        ...defaultValues,
      };
    case SchedulerEventType.UserAllocationSummary:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamAllocationSummary:
    case SchedulerEventType.CompanyAllocationSummary:
      return {
        eventColor: 'var(--yellow-30)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectTimeline:
    case SchedulerEventType.ProjectBudgetTimeline:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectAsALeafTimeline:
    case SchedulerEventType.BudgetAsALeafTimeline:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTimeOff:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.Holiday:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectPhase:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--green-20)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.DemandRole:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.DemandCapacity:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalSoftAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalHardAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalSoftAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalHardAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.PartnerFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TimeOffFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.HolidayFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.CapacityAllocationDiffFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectsAllocationInRoleFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectAllocationSummary:
      return {
        eventColor: 'var(--gray-30)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.BudgetAllocationSummary:
      return {
        eventColor: 'var(--gray-30)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ResourceAllocationSummary:
      return {
        eventColor: 'var(--resource-allocation-summary-color)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    default:
      return unreachable(type);
  }
};
